import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import uniqBy from 'lodash/uniqBy';

import { Params, ScenarioMember, useScenariosMembersList } from '@queries/scenarios';

import { YesNoFlag } from '../../../shared/types';
import { ScenarioSharedReduxState } from '../../shared/types';
import type { ReportRowData } from '../types';
import { getFieldName } from './utils';
import { reportsNamesSelector } from '../../shared/store/selectors';
import { useSelectedScenarioCompanyCodes, useSelectedScenarioCountryTaxType } from '../../shared/utils';

export const useFetchDetailsParams = ({
  baseScenarioId,
  compareScenarioId,
  reportRow,
  reportingCurrency,
}: {
  baseScenarioId: number,
  compareScenarioId?: number | null,
  reportRow: ReportRowData,
  reportingCurrency: ScenarioSharedReduxState['selectedCurrency'],
}) => {
  const selectedCompanyCodes = new Set(useSelectedScenarioCompanyCodes());
  const selectedCountryTaxTypes = useSelectedScenarioCountryTaxType();
  const reportNames = useSelector(reportsNamesSelector);

  const {
    data: baseScenarioCompanyCodes,
    isLoading: areBaseScenarioCompanyCodesLoading,
  } = useScenariosMembersList({
    scenarioId: baseScenarioId,
    scenarioEnabled: YesNoFlag.Y,
  });

  const {
    data: compareScenarioCompanyCodes,
    isLoading: areCompareScenarioCompanyCodesLoading,
  } = useScenariosMembersList({
    scenarioId: compareScenarioId || -1,
    scenarioEnabled: YesNoFlag.Y,
  }, { enabled: !!compareScenarioId });

  const reports = uniqBy(reportNames.map(reportName => ({
    reportName,
    fieldName: getFieldName(reportRow, reportName),
  })).filter(({ fieldName, reportName }) => !!fieldName && !!reportName), 'reportName');

  const getCompanyCode = ({ companyCode }: ScenarioMember) => companyCode;
  const selectedBaseScenarioCompanyCodes = baseScenarioCompanyCodes
    .map(getCompanyCode)
    .filter(cc => selectedCompanyCodes.has(cc));

  const selectedCompareScenarioCompanyCodes = compareScenarioCompanyCodes
    .map(getCompanyCode)
    .filter(cc => selectedCompanyCodes.has(cc));

  const params: Params[] = useMemo(() => [
    ...reports.map(({ reportName, fieldName }) => ({
      scenarioId: baseScenarioId,
      reportName,
      fieldName,
      companyCodes: selectedBaseScenarioCompanyCodes,
      countryTaxTypes: selectedCountryTaxTypes,
      reportingCurrency,
    })),
    ...compareScenarioId && selectedCompareScenarioCompanyCodes.length > 0
      ? reports.map(({ reportName, fieldName }) => ({
        scenarioId: compareScenarioId,
        reportName,
        fieldName,
        companyCodes: selectedCompareScenarioCompanyCodes,
        countryTaxTypes: selectedCountryTaxTypes,
        reportingCurrency: reportingCurrency || '',
      }))
      : [],
  ].filter(({ companyCodes }) => companyCodes.length), [
    baseScenarioId,
    compareScenarioId,
    reportingCurrency,
    reports,
    selectedCountryTaxTypes,
    selectedBaseScenarioCompanyCodes,
    selectedCompareScenarioCompanyCodes,
  ]);

  return {
    isLoading: areBaseScenarioCompanyCodesLoading || areCompareScenarioCompanyCodesLoading,
    params,
  };
};
